import { useCallback, useEffect, useRef, useState } from "react";
import { Plus, Pencil, Trash2, Package, ShoppingBag, Truck, ExternalLink, Loader2, Image, Mail, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, API, errMsg } from "@/lib/api";
import { useConfirm } from "@/context/ConfirmContext";
import { ShopProductForm } from "./ShopProductForm";
import { HeroAdmin } from "./HeroAdmin";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import newsletterDefaultImg from "@/assets/newsletter.webp";

const zl = (g) => new Intl.NumberFormat("pl-PL", { style: "currency", currency: "PLN" }).format((g || 0) / 100);

export function ShopAdmin() {
  const confirm = useConfirm();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [settings, setSettings] = useState(null);
  const [editing, setEditing] = useState(null); // product obj or {} for new
  const [loading, setLoading] = useState(true);
  const [tracking, setTracking] = useState({});
  const trk = (o) => (tracking[o.id] ?? (o.tracking_number || ""));

  const loadProducts = useCallback(() => api.get("/shop/admin/products").then((r) => setProducts(r.data || [])), []);
  const loadOrders = useCallback(() => api.get("/shop/admin/orders").then((r) => setOrders(r.data || [])), []);
  const loadSettings = useCallback(() => api.get("/shop/admin/settings").then((r) => setSettings(r.data)), []);

  useEffect(() => { Promise.all([loadProducts(), loadOrders(), loadSettings()]).finally(() => setLoading(false)); }, [loadProducts, loadOrders, loadSettings]);

  const del = async (p) => {
    if (!(await confirm({ title: "Usunąć produkt?", description: `Usunąć „${p.name}" ze sklepu?`, confirmText: "Usuń", danger: true }))) return;
    try { await api.delete(`/shop/admin/products/${p.id}`); toast.success("Usunięto"); loadProducts(); } catch (e) { toast.error(errMsg(e)); }
  };
  const setOrderStatus = async (o, status) => {
    try { await api.patch(`/shop/admin/orders/${o.id}`, { status, tracking_number: trk(o) }); toast.success(status === "Wysłane" ? "Status: Wysłane — wysłano e-mail do klienta" : "Zmieniono status"); loadOrders(); } catch (e) { toast.error(errMsg(e)); }
  };

  if (editing !== null) {
    return (
      <div className="rounded-2xl border border-border bg-card p-6">
        <h3 className="font-display text-xl font-bold uppercase mb-5">{editing.id ? "Edycja produktu" : "Nowy produkt sklepowy"}</h3>
        <ShopProductForm product={editing.id ? editing : null} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); loadProducts(); }} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold uppercase tracking-tight">Sklep internetowy</h2>
          <p className="text-sm text-muted-foreground">Zarządzaj publicznym sklepem EMPRO.</p>
        </div>
        <a href="/sklep" target="_blank" rel="noreferrer"><Button variant="outline" size="sm" className="gap-2"><ExternalLink className="h-4 w-4" />Otwórz sklep</Button></a>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger data-testid="shopadmin-tab-products" value="products" className="gap-2"><Package className="h-4 w-4" />Produkty ({products.length})</TabsTrigger>
          <TabsTrigger data-testid="shopadmin-tab-orders" value="orders" className="gap-2"><ShoppingBag className="h-4 w-4" />Zamówienia ({orders.length})</TabsTrigger>
          <TabsTrigger data-testid="shopadmin-tab-hero" value="hero" className="gap-2"><Image className="h-4 w-4" />Banery</TabsTrigger>
          <TabsTrigger data-testid="shopadmin-tab-settings" value="settings" className="gap-2"><Truck className="h-4 w-4" />Dostawa</TabsTrigger>
          <TabsTrigger data-testid="shopadmin-tab-newsletter" value="newsletter" className="gap-2"><Mail className="h-4 w-4" />Newsletter</TabsTrigger>
          <TabsTrigger data-testid="shopadmin-tab-brands" value="brands" className="gap-2"><ShieldCheck className="h-4 w-4" />Marki (GPSR)</TabsTrigger>
        </TabsList>

        <TabsContent value="products" className="mt-4">
          <Button data-testid="shop-add-product" onClick={() => setEditing({})} className="mb-4 gap-2 bg-amber-400 text-[#0D1117] hover:bg-amber-500 font-bold"><Plus className="h-4 w-4" />Dodaj produkt</Button>
          {loading ? <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /> : products.length === 0 ? <p className="text-sm text-muted-foreground">Brak produktów. Dodaj pierwszy, aby pojawił się w sklepie.</p> : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="shopadmin-products">
              {products.map((p) => (
                <div key={p.id} data-testid={`shopadmin-product-${p.id}`} className="rounded-xl border border-border bg-secondary overflow-hidden">
                  <div className="aspect-video bg-white overflow-hidden">{p.photos?.[0] ? <img src={`${API}/files/${p.photos[0]}`} alt="" className="h-full w-full object-contain p-2" /> : <div className="grid h-full place-items-center text-muted-foreground"><Package className="h-8 w-8" /></div>}</div>
                  <div className="p-3">
                    <div className="flex items-center gap-2"><span className={`h-2 w-2 rounded-full ${p.active ? "bg-emerald-500" : "bg-neutral-400"}`} /><span className="text-[11px] uppercase tracking-wider text-muted-foreground">{p.active ? "Opublikowany" : "Ukryty"}</span></div>
                    <p className="mt-1 font-semibold truncate">{p.name}</p>
                    <p className="text-sm font-bold">{zl(p.price_grosz)}{p.compare_at_grosz > p.price_grosz && <span className="ml-2 text-xs text-muted-foreground line-through">{zl(p.compare_at_grosz)}</span>}</p>
                    <div className="mt-2 flex gap-2">
                      <Button data-testid={`shop-edit-${p.id}`} onClick={() => setEditing(p)} size="sm" variant="outline" className="h-8 flex-1 gap-1.5"><Pencil className="h-3.5 w-3.5" />Edytuj</Button>
                      <Button data-testid={`shop-del-${p.id}`} onClick={() => del(p)} size="sm" variant="outline" className="h-8 px-2 border-rose-500/40 text-rose-400"><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="orders" className="mt-4">
          {orders.length === 0 ? <p className="text-sm text-muted-foreground">Brak zamówień sklepowych.</p> : (
            <div className="space-y-3" data-testid="shopadmin-orders">
              {orders.map((o) => (
                <div key={o.id} data-testid={`shop-order-${o.id}`} className="rounded-xl border border-border bg-secondary p-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="font-bold">{o.number} <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${o.payment_status === "COMPLETED" ? "bg-emerald-500/20 text-emerald-400" : "bg-amber-500/20 text-amber-400"}`}>{o.payment_status === "COMPLETED" ? "Opłacone" : o.payment_status}</span></p>
                      <p className="text-xs text-muted-foreground">{o.buyer?.first_name} {o.buyer?.last_name} · {o.buyer?.email} · {o.buyer?.phone}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-extrabold">{zl(o.total_grosz)}</span>
                      <Input data-testid={`shop-track-${o.id}`} value={trk(o)} onChange={(e) => setTracking((t) => ({ ...t, [o.id]: e.target.value }))} placeholder="Nr przesyłki" className="h-9 w-40" />
                      <Select value={o.status} onValueChange={(v) => setOrderStatus(o, v)}>
                        <SelectTrigger className="h-9 w-48"><SelectValue /></SelectTrigger>
                        <SelectContent>{(settings?.statuses || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="mt-3 border-t border-border pt-3 text-sm">
                    <p className="text-muted-foreground text-xs mb-1">Dostawa: {o.shipping?.method_name}{o.shipping?.inpost_point ? ` (${o.shipping.inpost_point})` : ""}{o.buyer?.street ? ` · ${o.buyer.street}, ${o.buyer.postal} ${o.buyer.city}` : ""}</p>
                    {(o.items || []).map((it, i) => <p key={i}>{it.quantity}× {it.name} {[it.color, it.size].filter(Boolean).join(" / ")} — {zl(it.unit_price_grosz * it.quantity)}</p>)}
                    {o.invoice?.want && <p className="mt-1 text-xs text-muted-foreground">FV: {o.invoice.company} NIP {o.invoice.nip}</p>}
                    {o.note && <p className="mt-1 text-xs italic text-muted-foreground">Uwagi: {o.note}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="hero" className="mt-4">
          <HeroAdmin />
        </TabsContent>

        <TabsContent value="settings" className="mt-4">
          {settings && <ShippingSettings settings={settings} onSaved={loadSettings} />}
        </TabsContent>

        <TabsContent value="newsletter" className="mt-4">
          <NewsletterList />
        </TabsContent>

        <TabsContent value="brands" className="mt-4">
          <BrandGpsrManager />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ShippingSettings({ settings, onSaved }) {
  const [methods, setMethods] = useState((settings.shipping_methods || []).map((m) => ({ ...m, price_zl: ((m.price_grosz || 0) / 100).toString().replace(".", ",") })));
  const [threshold, setThreshold] = useState(((settings.free_shipping_threshold_grosz || 0) / 100) || "");
  const [busy, setBusy] = useState(false);
  const upd = (i, k, v) => setMethods((m) => m.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/shop/admin/settings", {
        shipping_methods: methods.map(({ price_zl, ...m }) => ({ ...m, price_grosz: Math.round(parseFloat(String(price_zl).replace(",", ".")) * 100) || 0 })),
        free_shipping_threshold_grosz: Math.round(parseFloat(String(threshold).replace(",", ".") || "0") * 100) || 0,
      });
      toast.success("Zapisano ustawienia dostawy"); onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="max-w-2xl space-y-4" data-testid="shop-shipping-settings">
      {methods.map((m, i) => (
        <div key={m.id} className="grid grid-cols-[1fr_120px_auto] items-center gap-3 rounded-lg border border-border p-3">
          <Input value={m.name} onChange={(e) => upd(i, "name", e.target.value)} />
          <Input value={m.price_zl} onChange={(e) => upd(i, "price_zl", e.target.value)} placeholder="zł" />
          <label className="flex items-center gap-1.5 text-xs whitespace-nowrap"><input type="checkbox" checked={!!m.point_required} onChange={(e) => upd(i, "point_required", e.target.checked)} className="accent-amber-400" />Paczkomat</label>
        </div>
      ))}
      <div className="flex items-center gap-2"><label className="text-sm text-muted-foreground">Darmowa dostawa od (zł, 0 = wyłączone):</label><Input value={threshold} onChange={(e) => setThreshold(e.target.value)} className="w-28" /></div>
      <Button data-testid="shop-save-shipping" onClick={save} disabled={busy} className="gap-2 bg-amber-400 text-[#0D1117] hover:bg-amber-500 font-bold">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}Zapisz ustawienia dostawy</Button>
    </div>
  );
}

function NewsletterList() {
  const confirm = useConfirm();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imgId, setImgId] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const load = useCallback(() => api.get("/shop/admin/newsletter").then((r) => setRows(r.data || [])).finally(() => setLoading(false)), []);
  useEffect(() => { load(); api.get("/shop/admin/newsletter-image").then((r) => setImgId(r.data?.file_id || "")).catch(() => {}); }, [load]);

  const uploadImage = async (e) => {
    const file = (e.target.files || [])[0]; e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/files", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.put("/shop/admin/newsletter-image", { file_id: data.id });
      setImgId(data.id);
      toast.success("Zmieniono zdjęcie newslettera");
    } catch (err) { toast.error(errMsg(err)); } finally { setUploading(false); }
  };

  const resetImage = async () => {
    if (!(await confirm({ title: "Przywrócić domyślne zdjęcie?", description: "Popup newslettera wróci do grafiki domyślnej.", confirmText: "Przywróć" }))) return;
    try { await api.put("/shop/admin/newsletter-image", { file_id: "" }); setImgId(""); toast.success("Przywrócono domyślne zdjęcie"); } catch (e) { toast.error(errMsg(e)); }
  };

  const del = async (s) => {
    if (!(await confirm({ title: "Usunąć zapis?", description: s.email, confirmText: "Usuń", danger: true }))) return;
    try { await api.delete(`/shop/admin/newsletter/${s.id}`); toast.success("Usunięto"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const exportCsv = () => {
    const csv = "email,data\n" + rows.map((r) => `${r.email},${r.created_at || ""}`).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = "newsletter.csv"; a.click();
  };

  if (loading) return <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />;

  return (
    <div className="space-y-5" data-testid="shopadmin-newsletter">
      <div className="rounded-xl border border-border bg-secondary p-4" data-testid="newsletter-image-admin">
        <div className="flex items-start gap-4">
          <div className="w-40 shrink-0 rounded-lg overflow-hidden border border-border bg-white/5">
            <img src={imgId ? `${API}/files/${imgId}` : newsletterDefaultImg} alt="Podgląd zdjęcia newslettera" className="block w-full h-auto" />
          </div>
          <div className="flex-1 space-y-2">
            <p className="font-semibold">Zdjęcie w popupie newslettera</p>
            <p className="text-xs text-muted-foreground">Grafika wyświetlana w wsuwanym oknie zapisu na stronie sklepu. Zalecane proporcje pionowe ~4:5 (np. 600×750 px).</p>
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <Button data-testid="newsletter-image-upload" onClick={() => fileRef.current?.click()} disabled={uploading} size="sm" className="gap-2 bg-amber-400 text-[#0D1117] hover:bg-amber-500 font-bold">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Image className="h-4 w-4" />}Zmień zdjęcie
              </Button>
              {imgId && <Button data-testid="newsletter-image-reset" onClick={resetImage} size="sm" variant="outline">Przywróć domyślne</Button>}
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadImage} />
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">Zapisani do newslettera: <b className="text-foreground">{rows.length}</b></p>
        <Button data-testid="newsletter-export" onClick={exportCsv} disabled={!rows.length} size="sm" variant="outline" className="gap-2"><Mail className="h-4 w-4" />Eksport CSV</Button>
      </div>
      {rows.length === 0 ? <p className="text-sm text-muted-foreground">Brak zapisów do newslettera.</p> : (
        <div className="rounded-xl border border-border bg-secondary divide-y divide-border">
          {rows.map((s) => (
            <div key={s.id} data-testid={`newsletter-row-${s.id}`} className="flex items-center justify-between gap-3 px-4 py-2.5">
              <div className="min-w-0">
                <p className="font-medium truncate">{s.email}</p>
                <p className="text-xs text-muted-foreground">{s.created_at ? new Date(s.created_at).toLocaleString("pl-PL") : ""}</p>
              </div>
              <Button data-testid={`newsletter-del-${s.id}`} onClick={() => del(s)} size="sm" variant="outline" className="h-8 px-2 border-rose-500/40 text-rose-400"><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BrandGpsrManager() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => api.get("/shop/admin/brand-gpsr").then((r) => setRows(r.data || [])).finally(() => setLoading(false)), []);
  useEffect(() => { load(); }, [load]);
  if (loading) return <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />;
  return (
    <div className="max-w-2xl space-y-4" data-testid="brand-gpsr-manager">
      <p className="text-sm text-muted-foreground">Profil GPSR marki automatycznie uzupełnia producenta, adres i kontakt w każdym produkcie danej marki (rozpoznanie po polu „Marka”, bez różnicy wielkości liter) — przy dodaniu, imporcie i publikacji. Dane wpisane ręcznie w produkcie mają pierwszeństwo i nie są nadpisywane. Ostrzeżenia, model i certyfikaty pozostają indywidualne dla produktu.</p>
      {rows.map((r) => <BrandGpsrRow key={r.brand_lc} row={r} onSaved={load} />)}
      <div>
        <p className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">Nowy profil marki</p>
        <BrandGpsrRow row={{ brand: "" }} isNew onSaved={load} />
      </div>
    </div>
  );
}

function BrandGpsrRow({ row, isNew, onSaved }) {
  const [f, setF] = useState({ brand: row.brand || "", manufacturer: row.manufacturer || "", address: row.address || "", email: row.email || "", phone: row.phone || "", eu_responsible: row.eu_responsible || "", logo_file: row.logo_file || "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.brand.trim()) return toast.error("Podaj nazwę marki");
    setBusy(true);
    try {
      const { data } = await api.put(`/shop/admin/brand-gpsr/${encodeURIComponent(f.brand.trim())}`, f);
      toast.success(`Zapisano profil ${f.brand.trim()}${data.backfilled ? ` · uzupełniono ${data.backfilled} prod.` : ""}`);
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border border-border p-3 space-y-2" data-testid={`brand-gpsr-${isNew ? "new" : row.brand_lc}`}>
      <div className="grid sm:grid-cols-2 gap-2">
        <Input data-testid={`brand-gpsr-name-${isNew ? "new" : row.brand_lc}`} value={f.brand} onChange={(e) => set("brand", e.target.value)} placeholder="Marka (np. VESTI)" disabled={!isNew} />
        <Input value={f.manufacturer} onChange={(e) => set("manufacturer", e.target.value)} placeholder="Producent" />
        <Input value={f.address} onChange={(e) => set("address", e.target.value)} placeholder="Adres" className="sm:col-span-2" />
        <Input value={f.email} onChange={(e) => set("email", e.target.value)} placeholder="E-mail" />
        <Input value={f.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Telefon" />
        <Input value={f.eu_responsible} onChange={(e) => set("eu_responsible", e.target.value)} placeholder="Osoba odpowiedzialna w UE (tylko producenci spoza UE)" className="sm:col-span-2" />
      </div>
      <div className="flex items-center gap-3">
        {f.logo_file && <img src={`${API}/files/${f.logo_file}`} alt="logo" className="h-10 w-auto max-w-[120px] object-contain rounded border border-border bg-white p-1" />}
        <label className="cursor-pointer text-xs font-medium text-amber-600 hover:underline">
          {f.logo_file ? "Zmień logo marki" : "Wgraj logo marki"}
          <input data-testid={`brand-gpsr-logo-${isNew ? "new" : row.brand_lc}`} type="file" accept="image/*" className="hidden" onChange={async (e) => {
            const file = e.target.files?.[0]; if (!file) return;
            const fd = new FormData(); fd.append("file", file);
            try { const { data } = await api.post("/files", fd); set("logo_file", data.id); toast.success("Logo wgrane"); } catch (err) { toast.error(errMsg(err)); }
          }} />
        </label>
        {f.logo_file && <button type="button" onClick={() => set("logo_file", "")} className="text-xs text-rose-400 hover:underline">Usuń</button>}
      </div>
      <Button data-testid={`brand-gpsr-save-${isNew ? "new" : row.brand_lc}`} onClick={save} disabled={busy} size="sm" className="gap-2 bg-amber-400 text-[#0D1117] hover:bg-amber-500 font-bold">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}{isNew ? "Dodaj profil marki" : "Zapisz"}</Button>
    </div>
  );
}
